import React, { useId, useRef } from 'react';
import { Check, Circle, History, Loader2 } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Pipeline } from './api';
import './compact-workspace-header.css';

export type WorkspaceStage = 'architecture' | 'estimate' | 'pricing' | 'proposal';
export const WORKSPACE_STAGES: WorkspaceStage[] = ['architecture', 'estimate', 'pricing', 'proposal'];
export const STAGE_LABEL: Record<WorkspaceStage, string> = {
  architecture: 'Architecture', estimate: 'Estimate', pricing: 'Pricing', proposal: 'Proposal',
};

/** One approval gate for tabs, launch links and in-workspace navigation.
 * The explicit pricing state takes precedence over the legacy `priced` flag:
 * a saved pricing draft is never enough to unlock a proposal. */
export function approvedStageGates(pipeline: Pipeline | null): Record<WorkspaceStage, boolean> {
  if (pipeline?.unlocked) return pipeline.unlocked;
  const pricingApproved = pipeline?.pricing === 'approved'
    || (pipeline?.pricing === undefined && pipeline?.priced === true);
  return {
    architecture: true,
    estimate: pipeline?.architecture === 'approved',
    pricing: pipeline?.architecture === 'approved' && pipeline?.estimate === 'approved',
    proposal: pipeline?.architecture === 'approved' && pipeline?.estimate === 'approved' && pricingApproved,
  };
}

interface Props {
  active: WorkspaceStage;
  pipeline: Pipeline;
  available: Record<WorkspaceStage, boolean>;
  disabledReason: Partial<Record<WorkspaceStage, string>>;
  historical?: Partial<Record<WorkspaceStage, boolean>>;
  onSelect: (stage: WorkspaceStage) => void;
}

/** Navigation only. The owner supplies the existing role and approval gates. */
export default function WorkspaceStageNav({ active, pipeline, available, disabledReason, historical = {}, onSelect }: Props) {
  const layoutId = useId();
  const reducedMotion = useReducedMotion();
  const tabs = useRef<Partial<Record<WorkspaceStage, HTMLButtonElement | null>>>({});
  const status = (stage: WorkspaceStage) => stage === 'pricing'
    ? (pipeline.pricing ?? (pipeline.priced ? 'approved' : 'none')) : pipeline[stage];

  return (
    <div className="arch-stage-navigation arch-stage-navigation-compact" role="tablist" aria-label="Project stages">
      {WORKSPACE_STAGES.map((stage, index) => {
        const history = historical[stage] === true;
        const done = !history && status(stage) === 'approved';
        const inProgress = status(stage) === 'review' || status(stage) === 'draft';
        const running = stage !== 'pricing' && pipeline.running?.[stage];
        const stateLabel = history ? 'Saved history · read only' : running ? 'Generating' : done ? 'Approved' : inProgress ? 'In review' : 'Not started';
        return (
            <button
              key={stage}
              ref={(node) => { tabs.current[stage] = node; }}
              type="button" role="tab" id={`workspace-tab-${stage}`} aria-controls={`workspace-panel-${stage}`}
              aria-selected={active === stage} disabled={!available[stage]}
              tabIndex={active === stage ? 0 : -1}
              title={available[stage] ? `${STAGE_LABEL[stage]} · ${stateLabel}` : disabledReason[stage]}
              className={'arch-stage-tab' + (active === stage ? ' is-active' : '') + (done ? ' is-complete' : '')}
              onClick={() => onSelect(stage)}
              onKeyDown={(event) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const enabled = WORKSPACE_STAGES.filter((item) => available[item]);
                const at = enabled.indexOf(stage);
                const next = event.key === 'Home' ? enabled[0] : event.key === 'End' ? enabled[enabled.length - 1]
                  : enabled[(at + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length];
                if (next) { onSelect(next); tabs.current[next]?.focus(); }
              }}
            >
              {active === stage && <motion.span className="arch-stage-active-surface" layoutId={layoutId} transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 430, damping: 38 }} />}
              <span className={'arch-stage-state' + (running ? ' is-running' : '')} aria-label={stateLabel}>
                {history ? <History size={12} /> : running ? <Loader2 size={12} /> : done ? <Check size={11} /> : inProgress ? <Circle size={5} fill="currentColor" /> : <span>{index + 1}</span>}
              </span>
              <span className="arch-stage-copy"><strong>{STAGE_LABEL[stage]}</strong></span>
            </button>
        );
      })}
    </div>
  );
}

/** Keep visited editors mounted: switching stages must never discard a draft. */
export function WorkspaceScene({ stage, active, children }: { stage: WorkspaceStage; active: boolean; children: React.ReactNode }) {
  return <section id={`workspace-panel-${stage}`} role="tabpanel" aria-labelledby={`workspace-tab-${stage}`} aria-hidden={!active} inert={!active}
    className={'arch-stage-scene arch-stage-scene-' + stage + (active ? ' is-active' : '')}>{children}</section>;
}
